import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconRefresh, IconPlus } from '@tabler/icons-react';
import type {
  CreatePrinterRequest,
  PrinterAdminRow,
  UpdatePrinterRequest,
} from '@techgitter/contracts';
import {
  createPrinter,
  deletePrinter,
  fetchPrinters,
  updatePrinter,
} from '../services/printersApi';
import { resolveTransport } from '../services/printerHelpers';
import { PrinterEditDialog } from '../components/admin/PrinterEditDialog';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';

/** "192.168.50.242:9100" for tcp9100, the queue name for spooler. */
function formatTarget(p: PrinterAdminRow): string {
  const resolved = resolveTransport(p.engine, p.transport);
  if (resolved === 'spooler') return p.printerName || '—';
  if (p.host) return `${p.host}:${p.port ?? 9100}`;
  return '—';
}

export const PrintersAdminPage: React.FC = () => {
  const [items, setItems] = useState<PrinterAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editing, setEditing] = useState<PrinterAdminRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<PrinterAdminRow | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await fetchPrinters(signal, { includeInactive: true });
      setItems(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setLoadError(
        err instanceof Error ? err.message : 'Failed to load printers',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const dialogOpen = creating || editing !== null;

  const closeDialog = () => {
    if (saving) return;
    setCreating(false);
    setEditing(null);
    setSaveError(null);
  };

  const handleSave = async (
    payload: CreatePrinterRequest | UpdatePrinterRequest,
    id: string | null,
  ) => {
    setSaving(true);
    setSaveError(null);
    try {
      if (id === null) {
        const created = await createPrinter(payload as CreatePrinterRequest);
        setItems((current) => [...current, created]);
        setSnackbar({ open: true, message: 'Printer created', severity: 'success' });
      } else {
        const updated = await updatePrinter(id, payload as UpdatePrinterRequest);
        setItems((current) =>
          current.map((it) => (it.id === updated.id ? updated : it)),
        );
        setSnackbar({ open: true, message: 'Printer updated', severity: 'success' });
      }
      setCreating(false);
      setEditing(null);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save printer',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteSubmitting(true);
    try {
      await deletePrinter(deleting.id);
      setItems((current) => current.filter((it) => it.id !== deleting.id));
      setSnackbar({ open: true, message: 'Printer deleted', severity: 'success' });
      setDeleting(null);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete printer',
        severity: 'error',
      });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // Sort by site, then role — so all of a site's lanes sit together.
  const sortedItems = useMemo(() => {
    return [...items].sort(
      (a, b) =>
        a.siteId.localeCompare(b.siteId) || a.role.localeCompare(b.role),
    );
  }, [items]);

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSpacer />
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
        <ToolbarButton
          variant="primary"
          icon={IconPlus}
          onClick={() => {
            setSaveError(null);
            setCreating(true);
          }}
        >
          Add printer
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        {loadError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTableCard>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Site</DataTableHeadCell>
                  <DataTableHeadCell>Role</DataTableHeadCell>
                  <DataTableHeadCell>Engine</DataTableHeadCell>
                  <DataTableHeadCell>Transport</DataTableHeadCell>
                  <DataTableHeadCell>Target</DataTableHeadCell>
                  <DataTableHeadCell align="right">Delay ms</DataTableHeadCell>
                  <DataTableHeadCell>Active</DataTableHeadCell>
                  <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <DataTableEmpty colSpan={8}>No printers configured</DataTableEmpty>
                ) : (
                  sortedItems.map((item) => (
                    <DataTableRow key={item.id}>
                      <DataTableCell bold>{item.siteId}</DataTableCell>
                      <DataTableCell>{item.role}</DataTableCell>
                      <DataTableCell>{item.engine}</DataTableCell>
                      <DataTableCell>
                        {item.transport ?? (
                          <Chip>{resolveTransport(item.engine, item.transport) ?? 'auto'}</Chip>
                        )}
                      </DataTableCell>
                      <DataTableCell>{formatTarget(item)}</DataTableCell>
                      <DataTableCell align="right">{item.interJobDelayMs}</DataTableCell>
                      <DataTableCell>
                        <Chip tone={item.isActive ? 'success' : 'neutral'}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </Chip>
                      </DataTableCell>
                      <DataTableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSaveError(null);
                              setEditing(item);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleting(item)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </DataTableCell>
                    </DataTableRow>
                  ))
                )}
              </tbody>
            </DataTable>
          </DataTableCard>
        )}
      </Box>

      <PrinterEditDialog
        open={dialogOpen}
        printer={editing}
        isSaving={saving}
        error={saveError}
        onClose={closeDialog}
        onSave={handleSave}
      />

      <Dialog
        open={deleting !== null}
        onClose={() => !deleteSubmitting && setDeleting(null)}
      >
        <DialogTitle>Delete printer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete{' '}
            <strong>
              {deleting?.siteId}:{deleting?.role}
            </strong>
            ? This removes the lane and frees its (site, role) pair. To keep it
            but stop the agent using it, edit it and turn off “Active” instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
